import React, {
  useContext, useEffect, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { createUploadLink } from 'apollo-upload-client';
import { useSelector, useDispatch, batch } from 'react-redux';
import { ApolloProvider } from 'react-apollo';

import Apps from 'apps';
import {
  APP,
  API_URL,
  RONGCLOUD_APP_KEY,
  RONGCLOUD_CHATROOM_PREFIX,
} from 'appenv';
import {
  addMessage, setMessages, removeMessage, receiveMessage,
} from 'models/messages';
import { setConversations, updateConversation, addConversation } from 'models/conversations';
import attendeesByIds from 'network/attendeesByIds';
import groupChatJoin from 'network/groupChatJoin';
import { setTarget } from 'models/chatTarget';
import { setInfo } from 'models/rtcCallingInfo';
import { setCallStatus } from 'models/rtcCallingStatus';
import { fetchRongcloudGroupMemberList } from 'models/chatTargetGroupMembers';
import { selectExhibitorIds } from 'models/exhibitors';
import { EventDatetimeContext } from 'components/EventDatetime';
import ChatWindowSwitch711 from 'components/chat/chat_window_711/ChatWindowSwitch711';
import { useTranslation } from 'react-i18next';
import ChatContext from './chat/ChatContext';
import ChatWindowSwitch from './chat/ChatWindowSwitch';
import RtcChatBackdrop from './chat/RtcChatBackdrop';
import { UserSessionContext } from './UserSession';

const uploadLink = createUploadLink({
  uri: API_URL,
  headers: {
    'keep-alive': 'true',
  },
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: uploadLink,
});

const disableAnnouncement = ['hkstp608'];

const RongCloudChat = ({ children }) => {
  const { t } = useTranslation();
  const { userSession } = useContext(UserSessionContext);
  const { targetId, type } = useSelector((state) => state.chatTarget);
  const loginTime = useSelector((state) => state.loginTime);
  const [status, setStatus] = useState('');
  const [isChatWindowShow, setIsChatWindowShow] = useState(false);
  const [joiningChat, setJoiningChat] = useState(false);
  const dispatch = useDispatch();
  const [hasMoreMsg, setHasMoreMsg] = useState(false);
  const [showGroupMemberList, setShowGroupMemberList] = useState(false);
  const [chatRoomTitle, setChatRoomTitle] = useState('Chatroom');
  const boothIds = useSelector(selectExhibitorIds);
  const { hasEventStarted } = useContext(EventDatetimeContext);
  const rtcCallingInfo = useSelector((state) => state.rtcCallingInfo);
  const rtcCallingStatus = useSelector((state) => state.rtcCallingStatus);
  const [rtcAlert, setRtcAlert] = useState('');

  const needsRtcCall = false;
  const isChromeUser = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);

  const getConversationType = (targetType) => {
    let conversationType = '';
    switch (targetType) {
      case 'group':
        conversationType = window.RongIMLib.ConversationType.GROUP;
        break;
      case 'private':
        conversationType = window.RongIMLib.ConversationType.PRIVATE;
        break;
      case 'system':
        conversationType = window.RongIMLib.ConversationType.SYSTEM;
        break;
      default:
        break;
    }
    return conversationType;
  };

  const sendMessage = (receiverId, conversationType, toSendmessage, messageType) => {
    if (receiverId && toSendmessage && conversationType) {
      const {
        name,
        customAttendeeFields: {
          vexpo_chat_tag: tag,
          vexpo_chat_user_description: description,
        },
      } = userSession;
      const user = {
        name,
        tag,
        description,
      };
      let msg;
      switch (messageType) {
        case 'FileMessage':
          msg = new window.RongIMLib.FileMessage({
            name: toSendmessage.name,
            size: toSendmessage.size,
            type: toSendmessage.type,
            fileUrl: toSendmessage.fileUrl,
            user,
          });
          break;
        case 'AudioCallMessage':
          msg = new window.RongIMClient.RegisterMessage.AudioCallMessage({
            content: toSendmessage,
            messageName: 'AudioCallMessage',
            user,
          });
          break;
        case 'VideoCallMessage':
          msg = new window.RongIMClient.RegisterMessage.VideoCallMessage({
            content: toSendmessage,
            messageName: 'VideoCallMessage',
            user,
          });
          break;
        default:
          msg = new window.RongIMLib.TextMessage({
            content: toSendmessage,
            user,
          });
      }

      window.RongIMClient.getInstance().sendMessage(conversationType, receiverId, msg, {
        onSuccess: (message) => {
          message.id = message.messageUId || message.messageId;
          const messageStr = JSON.stringify(message);
          const messageJSON = JSON.parse(messageStr);
          batch(() => {
            dispatch(addMessage(messageJSON));
            dispatch(updateConversation({
              id: receiverId,
              changes: {
                latestMessage: messageJSON,
              },
            }));
          });
        },
        onError: (errorCode) => {
          const failedMsg = new window.RongIMLib.TextMessage({
            content: {
              content: toSendmessage,
              user: {
                name,
                tag,
                description,
              },
            },
            sentTime: moment().valueOf(),
            sentFailed: errorCode,
            messageId: moment().valueOf(),
            receiverId,
            senderUserId: userSession.rongcloudUserId,
          });
          const messageStr = JSON.stringify(failedMsg);
          const messageJSON = JSON.parse(messageStr);
          batch(() => {
            dispatch(addMessage(messageJSON));
            dispatch(updateConversation({
              id: receiverId,
              changes: {
                latestMessage: messageJSON,
              },
            }));
          });
        },
      });
      dispatch(updateConversation({
        id: receiverId,
        changes: {
          sentTime: moment().valueOf(),
        },
      }));
    }
  };

  const getHistoryMsg = useCallback(() => {
    if (targetId) {
      const conversationType = getConversationType(type);
      const count = 20;
      window.RongIMClient.getInstance().getHistoryMessages(conversationType, targetId, null, count, {
        onSuccess: (list, hasMsg) => {
          if (list.length > 0) {
            const listJSON = list.map((message) => {
              message.id = message.messageUId || message.messageId;
              const messageStr = JSON.stringify(message);
              const messageJSON = JSON.parse(messageStr);
              return messageJSON;
            });
            dispatch(setMessages(listJSON));
          }
          setHasMoreMsg(hasMsg);
        },
        onError: (error) => {
          console.log('Failed to get history messages', error);
        },
      });
    }
  }, [dispatch, type, targetId]);

  const connect = () => {
    const { rongcloudToken } = (userSession || {});
    if (!rongcloudToken) return;
    window.RongIMClient.connect(rongcloudToken, {
      onSuccess: () => {
        setStatus('connected');
        window.RongIMClient.getInstance().getConversationList({
          onSuccess: (list) => {
            batch(() => {
              if (!isEmpty(list)) {
                const privateIds = [];
                const conversationList = list.map((conversation) => {
                  const messageStr = JSON.stringify(conversation.latestMessage);
                  const messageJSON = JSON.parse(messageStr);
                  let conversationType = '';
                  switch (conversation.conversationType) {
                    case 3:
                      conversationType = 'group';
                      break;
                    case 1:
                      conversationType = 'private';
                      break;
                    case 6:
                      conversationType = 'system';
                      break;
                    default:
                      break;
                  }
                  const newObject = {
                    id: conversation.targetId,
                    unreadMessageCount: conversation.unreadMessageCount,
                    latestMessage: messageJSON,
                    conversationType,
                    sentTime: conversation.sentTime,
                  };
                  if (conversation.conversationType === 1) {
                    privateIds.push(conversation.targetId);
                  }
                  return newObject;
                });
                dispatch(setConversations({
                  list: conversationList,
                  ids: privateIds,
                }));
              }
              if (!disableAnnouncement.includes(APP)) {
                dispatch(addConversation({
                  id: `${RONGCLOUD_CHATROOM_PREFIX}_announcement`,
                  type: 'system',
                }));
              }
            });
          },
          onError: (error) => console.log('fail to fetch conversation list', error),
        }, null, 200);
      },
      onError: (error) => console.log('fail to connect rongcloud im', error),
    });
  };

  const disconnect = () => {
    try {
      window.RongIMClient.getInstance().disconnect();
    } catch (e) {
      console.error(e);
    }
  };

  async function receiveCall(message) {
    const {
      content,
      conversationType,
      targetId: rtcTargetId,
    } = message;
    const attendees = await attendeesByIds([rtcTargetId]);
    const attendee = attendees[0] || {};
    batch(() => {
      dispatch(setInfo({
        conversationType,
        rtcTargetId,
        mediaType: content.mediaType,
        rtcTargetName: attendee.name,
        inviter: content.inviter,
      }));
      dispatch(setCallStatus('being_called'));
    });
    setJoiningChat(true);
  }

  const call = (mediaType, rtcTargetName) => {
    if (targetId && rtcCallingStatus === 'available') {
      if (isChromeUser) {
        const params = {
          conversationType: window.RongIMLib.ConversationType.PRIVATE,
          targetId,
          inviteUserIds: [targetId],
          mediaType,
        };
        const { rongcloudUserId } = userSession;
        window.rongCallLib.call(params, (error) => {
          if (error) {
            console.error('call init failed', error);
          } else {
            batch(() => {
              dispatch(setCallStatus('waiting_for_response'));
              dispatch(setInfo(
                {
                  conversationType: window.RongIMLib.ConversationType.PRIVATE,
                  rtcTargetId: targetId,
                  rtcTargetName,
                  inviter: rongcloudUserId,
                  mediaType,
                },
              ));
            });
            setRtcAlert(t('call.connecting', 'Connecting...'));
          }
        });
      } else {
        // eslint-disable-next-line no-alert
        alert(t(
          'call.not_supported',
          'We are sorry that our Audio / Video call services only supports Chrome browser.',
        ));
      }
    }
  };

  const accept = () => {
    const { conversationType, rtcTargetId, mediaType } = rtcCallingInfo;
    window.rongCallLib.accept({
      conversationType,
      targetId: rtcTargetId,
      mediaType,
    }, (error) => {
      if (error) {
        console.error('Failed to accept call', error);
      } else {
        dispatch(setCallStatus('calling'));
      }
    });
  };

  const reject = () => {
    const { conversationType, rtcTargetId } = rtcCallingInfo;
    window.rongCallLib.reject({
      conversationType,
      targetId: rtcTargetId,
    }, (error) => {
      if (error) {
        console.error('Failed to reject call', error);
      } else {
        batch(() => {
          dispatch(setInfo({}));
          dispatch(setCallStatus('available'));
        });
      }
    });
  };

  const hangup = (callType, callTargetId, message) => {
    const { conversationType, rtcTargetId, mediaType } = rtcCallingInfo;
    window.rongCallLib.hungup({
      conversationType: conversationType || callType,
      targetId: rtcTargetId || callTargetId,
    }, (error) => {
      if (error) {
        console.error('Failed to hang up call', error);
      } else {
        dispatch(setCallStatus('available'));
        setRtcAlert(t('call.ended', 'Call ended'));
      }
    });
    if (message) {
      const { content: { reason }, callInfo } = message;
      if (reason === 12) {
        sendMessage(message.targetId, callType, t('call.declined', 'Declined'), callInfo.mediaType === 1 ? 'AudioCallMessage' : 'VideoCallMessage');
      } else if (reason === 15) {
        sendMessage(message.targetId, callType, t('call.no_answer', 'Not answer'), callInfo.mediaType === 1 ? 'AudioCallMessage' : 'VideoCallMessage');
      }
    } else if (rtcCallingStatus === 'waiting_for_response') {
      sendMessage(rtcTargetId, conversationType, t('call.cancelled', 'Cancelled'), mediaType === 1 ? 'AudioCallMessage' : 'VideoCallMessage');
    }
  };

  const videoWatcher = (result) => {
    const { type: resultType } = result;
    const boxEl = document.getElementById('rtc-container');
    if (resultType === 'added') {
      const video = result.data;
      const className = result.isLocal ? 'isLocal' : '';
      video.setAttribute('class', className);
      boxEl.appendChild(video);
    } else {
      boxEl.innerHTML = '';
    }
  };

  const commandWatcher = (message) => {
    const {
      content,
      messageType,
      conversationType,
    } = message;
    const {
      rongcloudUserId,
    } = userSession;
    switch (messageType) {
      case 'InviteMessage':
        receiveCall(message);
        break;
      case 'AcceptMessage':
        dispatch(setCallStatus('calling'));
        break;
      case 'HungupMessage':
        hangup(conversationType, message.targetId, message);
        batch(() => {
          dispatch(setCallStatus('available'));
          dispatch(setInfo({}));
        });
        break;
      case 'SummaryMessage':
        if (content.duration > 0) {
          if (rongcloudUserId === message.senderUserId) {
            const duration = moment.duration(content.duration);
            const seconds = duration.seconds();
            const minutes = duration.minutes();
            const durationMessage = t('call.duration', `Duration:  ${minutes}:${seconds}`, { minutes, seconds });
            if (content.mediaType === 1) {
              sendMessage(message.targetId, conversationType, durationMessage, 'AudioCallMessage');
            } else if (content.mediaType === 2) {
              sendMessage(message.targetId, conversationType, durationMessage, 'VideoCallMessage');
            }
          }
        }
        if (content.status === 5 || content.status === 15) {
          batch(() => {
            dispatch(setCallStatus('available'));
            dispatch(setInfo({}));
          });
        }
        break;
      default:
        break;
    }
  };

  const registerMessageType = (messageName, objectName) => {
    const isCounted = true;
    const isPersited = true;
    const mesasgeTag = new window.RongIMLib.MessageTag(isCounted, isPersited);
    const prototypes = ['content', 'messageName', 'user'];
    window.RongIMClient.registerMessageType(messageName, objectName, mesasgeTag, prototypes);
  };

  const initialize = () => {
    if (!isEmpty(userSession) || APP !== 'ms624') {
      window.RongIMClient.init(RONGCLOUD_APP_KEY);
      window.RongIMLib.RongIMEmoji.init();
      window.RongIMClient.setConnectionStatusListener({
        onChanged: (currentStatus) => {
          switch (currentStatus) {
            case window.RongIMLib.ConnectionStatus.CONNECTED:
              setStatus('connected');
              break;
            case window.RongIMLib.ConnectionStatus.CONNECTING:
              setStatus('connecting');
              break;
            case window.RongIMLib.ConnectionStatus.DISCONNECTED:
              setStatus('disconnected');
              break;
            case window.RongIMLib.ConnectionStatus.KICKED_OFFLINE_BY_OTHER_CLIENT:
              // eslint-disable-next-line no-alert
              alert(t(
                'call.logged_in_other',
                'Your account has been logged-in from other tab/browser. You must refresh the current browser to use the Chat function.',
              ));
              setStatus('error');
              break;
            case window.RongIMLib.ConnectionStatus.DOMAIN_INCORRECT:
              setStatus('error');
              break;
            case window.RongIMLib.ConnectionStatus.NETWORK_UNAVAILABLE:
              setStatus('offline');
              connect();
              break;
            default:
              break;
          }
        },
      });

      window.RongIMClient.setOnReceiveMessageListener({
        onReceived: (message) => {
          if (message.sentTime > loginTime) {
            message.id = message.messageUId || message.messageId;
            const messageStr = JSON.stringify(message);
            const messageJSON = JSON.parse(messageStr);
            switch (message.messageType) {
              case window.RongIMClient.MessageType.TextMessage:
              case window.RongIMClient.MessageType.FileMessage:
                dispatch(receiveMessage(messageJSON));
                break;
              case window.RongIMClient.MessageType.ImageMessage:
              case window.RongIMClient.MessageType.HQVoiceMessage:
              case window.RongIMClient.MessageType.RichContentMessage:
              case window.RongIMClient.MessageType.UnknownMessage:
              default:
                break;
            }
          }
        },
      });

      if (needsRtcCall && isChromeUser) {
        const config = {
          timeout: 20000,
          RongIMLib: window.RongIMLib,
          RongRTC: window.RongRTC,
        };
        window.rongCallLib = window.RongCallLib.init(config);

        window.rongCallLib.videoWatch(videoWatcher);

        window.rongCallLib.commandWatch(commandWatcher);
      }
      registerMessageType('AudioCallMessage', 's:audio');
      registerMessageType('VideoCallMessage', 's:video');
      connect();
    }
  };

  const resendMessage = (message) => {
    dispatch(removeMessage(message.id));
    sendMessage(message.targetId, message.conversationType, message.content.content, message.messageType);
  };

  useEffect(() => {
    if (targetId && type && !joiningChat) {
      getHistoryMsg();
    }
  }, [type, targetId, joiningChat, getHistoryMsg]);

  useEffect(() => {
    if (!isEmpty(boothIds) && status !== 'connected') {
      initialize();
    }
  }, [boothIds]);

  async function joinGroupChat(groupId) {
    setJoiningChat(true);
    const { authToken } = userSession;
    const success = await groupChatJoin(authToken, groupId);
    if (success) {
      setJoiningChat(false);
      dispatch(fetchRongcloudGroupMemberList({ groupId }));
    }
  }

  const setChatTarget = (id, targetType, title) => {
    setShowGroupMemberList(false);
    batch(() => {
      dispatch(setTarget({ targetId: id, type: targetType, roomName: title }));
      if (id && targetType) {
        setChatRoomTitle(title || id);
        if (targetType === 'group') {
          joinGroupChat(id);
        }
        dispatch(updateConversation({
          id,
          changes: {
            unreadMessageCount: 0,
          },
        }));
        dispatch(addConversation({
          id,
          type: targetType,
        }));
        const conversationType = getConversationType(targetType);
        window.RongIMClient.getInstance().clearUnreadCount(conversationType, id, {
          onSuccess: () => { },
          onError: () => { },
        });
      }
    });
  };

  const closeChatWindow = () => {
    setChatTarget(null, null);
    setIsChatWindowShow(false);
    setHasMoreMsg(false);
  };

  const showChatWindow = (id, chatType, title) => {
    const {
      role: userRole,
      customAttendeeFields: {
        vexpo_exhibitor_booth_id: exhibitorBoothID,
      },
    } = userSession;
    const exhibitorChatroomID = `${RONGCLOUD_CHATROOM_PREFIX}_booth_${exhibitorBoothID}`;
    if (chatType === 'group' && userRole === 'exhibitor' && !isEmpty(id) && id !== exhibitorChatroomID) {
      // eslint-disable-next-line no-alert
      alert(t(
        'chat.no_permission',
        'You have no permission to access other booths.',
      ));
    } else {
      setIsChatWindowShow(true);
      setChatTarget(id, chatType, title);
    }
  };

  const checkIfLanding = () => window.location.pathname.startsWith('/landing');
  const shouldChatWindowDisplay = () => (
    !checkIfLanding()
    && hasEventStarted
    && !isEmpty(boothIds)
    && !isEmpty(userSession)
    && !['ms624', Apps.Jetro817].includes(APP)
  );

  return (
    <ChatContext.Provider
      value={{
        targetId,
        type,
        showChatWindow,
        closeChatWindow,
        disconnect,
        chatroomAvailable: shouldChatWindowDisplay(),
        rtcAlert,
        setRtcAlert,
      }}
    >
      {children}
      {
        shouldChatWindowDisplay() && (
          <ApolloProvider client={client}>
            {
              APP !== 'scmp711' ? (
                <ChatWindowSwitch
                  status={status}
                  joiningChat={joiningChat}
                  sendMessage={sendMessage}
                  resendMessage={resendMessage}
                  getHistoryMsg={getHistoryMsg}
                  chatRoomTitle={chatRoomTitle}
                  hasMoreMsg={hasMoreMsg}
                  setChatTarget={setChatTarget}
                  isChatWindowShow={isChatWindowShow}
                  setIsChatWindowShow={setIsChatWindowShow}
                  setChatRoomTitle={setChatRoomTitle}
                  call={call}
                  showGroupMemberList={showGroupMemberList}
                  setShowGroupMemberList={setShowGroupMemberList}
                />
              ) : (
                <ChatWindowSwitch711
                  status={status}
                  joiningChat={joiningChat}
                  sendMessage={sendMessage}
                  resendMessage={resendMessage}
                  getHistoryMsg={getHistoryMsg}
                  chatRoomTitle={chatRoomTitle}
                  hasMoreMsg={hasMoreMsg}
                  setChatTarget={setChatTarget}
                  isChatWindowShow={isChatWindowShow}
                  setIsChatWindowShow={setIsChatWindowShow}
                  setChatRoomTitle={setChatRoomTitle}
                  showGroupMemberList={showGroupMemberList}
                  setShowGroupMemberList={setShowGroupMemberList}
                />
              )
            }
          </ApolloProvider>
        )
      }
      {
        shouldChatWindowDisplay() && needsRtcCall && (
          <RtcChatBackdrop
            accept={accept}
            hangup={hangup}
            reject={reject}
          />
        )
      }
    </ChatContext.Provider>
  );
};

RongCloudChat.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RongCloudChat;
